import { useRouter as useNavigation } from 'next/navigation';
import { twMerge } from 'tailwind-merge';

import { Link } from '../../../../front/src/components/Link';
import { WebsiteCustomLogo } from '../../../../front/src/contributor/Website/WebsiteLogo';
import { useCleanRoutes } from '../../../../front/src/hooks/useCleanRoutes';
import { useConnectedRouter } from '../../../../front/src/hooks/useConnectedRouter';
import { useIsMobile } from '../../../../front/src/hooks/useIsMobile';
import { useWebsite } from '../../../../front/src/hooks/useWebsite';
import { ReactComponent as IconArrowPrev } from '../../../../front/src/icons/icon-arrow-previous.svg';
import { useAppSelector } from '../../../../front/src/redux/hooks';
import { selectPreviousPageParams } from '../../../../front/src/redux/slices/utils-slice';
import { mkMessageAnchor } from '../../../../front/src/thread/ThreadMessages/messageAnchor';
import type { PageConfig } from '../../../../front/src/types/debate-types';
import { ssrRevalidateHomeData } from '../../utils/ssrRevalidate';

type HeaderLogoProps = {
  pageConfig?: PageConfig;
};

export const HeaderLogo = ({ pageConfig }: HeaderLogoProps) => {
  const router = useConnectedRouter();
  const navigation = useNavigation();
  const { baseUrl, key: publicKey } = useWebsite();
  const { cleanRoute } = useCleanRoutes();

  const isMob = useIsMobile();
  const previousPage = pageConfig?.previousPage;

  // A selector is used here to let pages override previousPageParams,
  // e.g. when the server initial props could not get a value because of missing authentication (case of a back button from a yakli page).
  const previousPageParams = useAppSelector(selectPreviousPageParams);
  const previousPageParamsToHandle = previousPageParams || pageConfig?.previousPageParams;

  const backTarget = (isMob && previousPage) || 'home';
  // Sans `previousPage` la cible est la home : y passer les params d'une page précédente ferait
  // sérialiser un `slug` résiduel en query (`/?slug=…`), par le même chemin que le `messageId`.
  const backParams = isMob && previousPage ? previousPageParamsToHandle : undefined;

  // B1_02 — `messageId` n'est PLUS JAMAIS sérialisé en query string. `makeCleanRoute2` envoie en query
  // tout paramètre absent du pattern de route : le passer ici produisait `/t/<slug>?messageId=<id>` dans
  // le HTML servi, puis `/?messageId=<id>` une fois la page hydratée (previousPage vaut 'home' sur la page
  // sujet) — deux familles d'URL crawlées en masse par Googlebot et jamais indexées. Le retour au message
  // passe désormais par le fragment `#message_<id>` (contrat C1), qui désigne la même ancre sans créer
  // d'URL distincte pour un moteur. `as` est transmis tel quel par <Link> à next/link, qui formate le
  // fragment et applique le basePath (`/forums` sur Beauté Test) — aucun fichier transverse à modifier.
  const messageId = typeof router.query.messageId === 'string' ? router.query.messageId : undefined;
  const backToMessage =
    backTarget === 'topic' && messageId
      ? {
          ...cleanRoute({ pathname: backTarget, query: backParams as Record<string, string> }).as,
          hash: mkMessageAnchor(messageId),
        }
      : undefined;

  const handleLogoClick = async (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();

    const currentPath = router.asPath;

    await ssrRevalidateHomeData(baseUrl, publicKey); // Update static page (Home)

    if (currentPath === '/') {
      navigation.refresh(); // Refresh current page to get rid of cache

      // Trigger custom event to refetch user-specific data after refresh
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('homeRefreshComplete'));
      }, 100);
    } else {
      // Listen events to auto refresh once on home page
      router.events.on('routeChangeComplete', handleRouteChange);

      await router.push('/');

      function handleRouteChange(url: string) {
        if (url === '/') {
          navigation.refresh();

          // Trigger custom event to refetch user-specific data after refresh
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('homeRefreshComplete'));
          }, 100);
          // Remove event listener callback
          router.events.off('routeChangeComplete', handleRouteChange);
        }
      }
    }
  };

  // Both icons present and hidden by classes to avoid next error caused by difference between client and server side rendering.
  return (
    <div className="HeaderLogo">
      <Link
        to={backTarget}
        {...(backParams && { params: backParams })}
        {...(backToMessage && { as: backToMessage })}
      >
        <a
          className="cursor-pointer flex items-center -ml-3 pl-[14px] min-w-0 max-w-[30vw] md:flex-col"
          onClick={!isMob || !previousPage ? handleLogoClick : undefined}
        >
          <IconArrowPrev
            className={twMerge('pointer-events-none', !!previousPage ? 'md:hidden' : 'hidden')}
            width="20px"
            height="20px"
          />
          <WebsiteCustomLogo className={twMerge('max-h-[36px]', !!previousPage && '_md:hidden')} />
        </a>
      </Link>
    </div>
  );
};
